/**
 * LivePanel — the live stage of the campaign canvas.
 *
 * One row per platform the campaign runs on: live status and daily budget
 * read from the platform, results by the campaign's goal, cost per result,
 * and controls that act on the platform (pause/resume, set budget). Below
 * it: leads captured from the ads, how each creative is doing, and
 * recommendations that can be applied in one click when Admeru can carry
 * them out.
 *
 * Platform status rows are driven by real platform-assignment records so
 * per-account publish/fail/scheduled states stay accurate after the
 * background scheduler fires; organic posts keep the simple status view.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '@/services/api';
import type { CampaignState } from './types';
import { formatCount, formatMoney, formatPct, resultNoun, costPerLabel } from '@/utils/analyticsFormat';

export interface LivePanelProps {
  state: CampaignState;
  conversationId?: string;
}

// ── Types ────────────────────────────────────────────────────────────────────

type AssignmentStatus = 'published' | 'scheduled' | 'failed' | 'pending' | 'publishing';

interface LiveCampaign {
  platform: string;               // publish name (meta, google_ads, pinterest, twitter)
  analytics_platform: string;
  label: string;
  action_campaign_id: string;     // id for publisher calls
  platform_campaign_id: string;   // as stored on the publish record
  account_id: string | null;
  account_name?: string;
  days_running: number;
  has_data: boolean;
  result_type: string;
  result_count: number;
  cost_per_result: number | null;
  currency: string | null;
  spend: number | null;
  clicks: number;
  ctr: number | null;
  roas: number | null;
  conversions_value: number | null;
}

interface Recommendation {
  id: string;
  kind: string;
  severity: 'info' | 'suggest' | 'act';
  title: string;
  reason: string;
  platform: string;
  platform_campaign_id: string;
  account_id: string | null;
  apply: { type: 'budget'; increase_pct: number } | { type: 'pause' } | null;
}

interface CreativeRow {
  concept_id: string;
  spend: number;
  spend_share: number;
  ctr: number | null;
  result_count: number;
  cost_per_result: number | null;
  verdict: 'winner' | 'leading' | 'close' | 'behind' | 'too_early';
}

interface CreativeTest {
  platform: string;
  label: string;
  result_type: string;
  currency: string | null;
  status: 'no_data' | 'collecting' | 'leading' | 'winner';
  creatives: CreativeRow[];
}

interface LiveData {
  campaigns: LiveCampaign[];
  summary: { spend: number | null; conversions: number; currency: string | null; cost_per_result: number | null; ctr: number | null } | null;
  recommendations: Recommendation[];
  creative_tests: CreativeTest[];
  leads: { total: number; new: number; calls: number };
}

interface PlatformLive {
  status: string | null;
  budget_level: 'campaign' | 'ad_set';
  daily_budget: number | null;
  lifetime_budget: number | null;
  currency: string | null;
  budget_editable: boolean;
}

interface AssignmentRow {
  key: string;
  platform: string;
  accountName?: string;
  status: AssignmentStatus;
  postUrl?: string;
  publishType?: 'paid_ad' | 'organic_post';
  platformCampaignId?: string;
}

// ── Small pieces ─────────────────────────────────────────────────────────────

const StatusPill: React.FC<{ status: AssignmentStatus }> = ({ status }) => {
  const cfg: Record<AssignmentStatus, { label: string; cls: string }> = {
    published: { label: 'Published', cls: 'bg-ok-soft text-ok' },
    scheduled: { label: 'Scheduled', cls: 'bg-ok-soft text-ok' },
    publishing: { label: 'Publishing…', cls: 'bg-work-soft text-work' },
    pending: { label: 'Pending', cls: 'bg-inset text-ink-3' },
    failed: { label: 'Failed', cls: 'bg-bad-soft text-bad' },
  };
  const { label, cls } = cfg[status] ?? cfg.pending;
  return <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-medium ${cls}`}>{label}</span>;
};

/** What the platform says right now (Active / Paused / In review...). */
const LiveStatus: React.FC<{ live?: PlatformLive | null; loading: boolean }> = ({ live, loading }) => {
  if (loading) return <span className="text-[11px] text-ink-3">Checking…</span>;
  if (!live?.status) return <span className="text-[11px] text-ink-3" title="Couldn't read the status from the platform">Status unknown</span>;
  const s = live.status.toUpperCase();
  const active = s === 'ACTIVE' || s === 'ENABLED';
  const paused = s === 'PAUSED';
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-medium ${
      active ? 'bg-success-soft text-success' : paused ? 'bg-surface text-ink-2' : 'bg-warning-soft text-warning'
    }`}>
      <span className={`h-1.5 w-1.5 rounded-full ${active ? 'bg-success' : paused ? 'bg-ink-3' : 'bg-warning'}`} />
      {active ? 'Active' : paused ? 'Paused' : s.charAt(0) + s.slice(1).toLowerCase().replace(/_/g, ' ')}
    </span>
  );
};

const platformColor = (p: string) => {
  const m: Record<string, string> = {
    meta: '#1877F2', facebook: '#1877F2', google: '#4285F4', google_ads: '#4285F4',
    pinterest: '#E60023', tiktok: '#000000', twitter: '#1DA1F2', x: '#1DA1F2',
  };
  return m[p.toLowerCase()] ?? '#9ca3af';
};

const platformLabel = (p: string) => {
  const m: Record<string, string> = {
    meta: 'Meta', facebook: 'Facebook', google: 'Google', google_ads: 'Google', pinterest: 'Pinterest',
    tiktok: 'TikTok', twitter: 'X', x: 'X',
  };
  return m[p.toLowerCase()] ?? p.charAt(0).toUpperCase() + p.slice(1);
};

const PlatformIcon: React.FC<{ platform: string; size?: 'sm' | 'md' }> = ({ platform, size = 'md' }) => (
  <span
    className={`flex flex-shrink-0 items-center justify-center rounded-md font-bold text-white ${size === 'sm' ? 'h-5 w-5 text-[10px]' : 'h-6 w-6 text-[11px]'}`}
    style={{ backgroundColor: platformColor(platform) }}
  >
    {platform.slice(0, 1).toUpperCase()}
  </span>
);

const Kpi: React.FC<{ label: string; value: string; hint?: string }> = ({ label, value, hint }) => (
  <div className="rounded-md bg-surface px-2.5 py-2" title={hint}>
    <span className="block text-[10px] uppercase tracking-wide text-ink-3">{label}</span>
    <span className="text-sm font-semibold text-ink">{value}</span>
  </div>
);

const VERDICT: Record<CreativeRow['verdict'], { label: string; cls: string }> = {
  winner: { label: 'Winner', cls: 'bg-ok text-card' },
  leading: { label: 'Leading', cls: 'bg-ok-soft text-ok' },
  close: { label: 'Close', cls: 'bg-work-soft text-work' },
  behind: { label: 'Behind', cls: 'bg-inset text-navy-ink' },
  too_early: { label: 'Too early', cls: 'bg-warn-soft text-warn' },
};

// ── Main component ───────────────────────────────────────────────────────────

export const LivePanel: React.FC<LivePanelProps> = ({ state, conversationId }) => {
  const navigate = useNavigate();
  const launch = state.launch ?? {};
  const split = state.budget?.split ?? {};
  const concepts = (state.creativeSet ?? []).filter((c) => c.included !== false);
  const conceptName = useMemo(
    () => Object.fromEntries(concepts.map((c) => [c.id, c.concept ?? c.id])) as Record<string, string>,
    [concepts],
  );
  const isOrganic = launch.mode === 'organic';

  // ── Publish records (all statuses, paid and organic) ──────────────────────
  const [rows, setRows] = useState<AssignmentRow[] | null>(null);
  useEffect(() => {
    if (!conversationId) return;
    apiClient
      .get(`/api/v1/conversations/${conversationId}/platform-assignments`)
      .then((resp) => {
        const assignments: any[] = resp.data?.assignments ?? [];
        if (assignments.length === 0) return; // fall through to static rows
        // Latest per platform + publish type (a paid ad and an organic post on the same platform are distinct)
        const byKey = new Map<string, any>();
        for (const a of assignments) {
          const key = `${a.platform}:${a.publish_type ?? 'paid_ad'}`;
          const existing = byKey.get(key);
          if (!existing || (a.published_at ?? '') > (existing.published_at ?? '')) byKey.set(key, a);
        }
        setRows(Array.from(byKey.values()).map((a) => ({
          key: a.id ?? a.platform,
          platform: a.platform,
          accountName: a.account_name,
          status: (a.status === 'published' ? 'published' : a.status) as AssignmentStatus,
          postUrl: a.organic_post_urls?.[0],
          publishType: a.publish_type ?? 'paid_ad',
          platformCampaignId: a.platform_campaign_id,
        })));
      })
      .catch(() => {}); // non-critical — static fallback stays
  }, [conversationId]);

  // ── Numbers, creative test, leads, recommendations ────────────────────────
  const [data, setData] = useState<LiveData | null>(null);
  const loadData = useCallback(() => {
    if (!conversationId || isOrganic) return;
    apiClient
      .get(`/api/v1/analytics/conversation/${conversationId}/monitor`)
      .then((r) => setData(r.data))
      .catch(() => {}); // KPIs simply stay pending
  }, [conversationId, isOrganic]);
  useEffect(() => {
    loadData();
    const t = setInterval(loadData, 5 * 60000); // platforms report roughly hourly
    return () => clearInterval(t);
  }, [loadData]);

  // ── Live status + budget, read from each platform ─────────────────────────
  const [live, setLive] = useState<Record<string, PlatformLive | null>>({});
  const [liveLoading, setLiveLoading] = useState<Record<string, boolean>>({});
  const liveUrl = (c: { platform: string; action_campaign_id?: string; platform_campaign_id: string; account_id: string | null }, suffix: string) => {
    const id = encodeURIComponent(c.action_campaign_id ?? c.platform_campaign_id);
    const acct = c.account_id ? `account_id=${encodeURIComponent(c.account_id)}` : '';
    const join = suffix.includes('?') ? '&' : '?';
    return `/api/v1/publisher/${c.platform}/campaigns/${id}/${suffix}${acct ? join + acct : ''}`;
  };
  const loadLive = useCallback((c: LiveCampaign) => {
    setLiveLoading((s) => ({ ...s, [c.action_campaign_id]: true }));
    apiClient.get(liveUrl(c, 'live'))
      .then((r) => setLive((s) => ({ ...s, [c.action_campaign_id]: r.data })))
      .catch(() => setLive((s) => ({ ...s, [c.action_campaign_id]: null })))
      .finally(() => setLiveLoading((s) => ({ ...s, [c.action_campaign_id]: false })));
  }, []);
  useEffect(() => {
    data?.campaigns.forEach((c) => { if (!(c.action_campaign_id in live)) loadLive(c); });
  }, [data, live, loadLive]);

  // ── Actions ────────────────────────────────────────────────────────────────
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<{ key: string; text: string; ok: boolean } | null>(null);
  const [budgetDraft, setBudgetDraft] = useState<Record<string, string>>({});

  const setStatus = async (c: LiveCampaign, next: 'ACTIVE' | 'PAUSED') => {
    setBusy(c.action_campaign_id); setMessage(null);
    try {
      await apiClient.patch(liveUrl(c, `status?status=${next}`));
      setMessage({ key: c.action_campaign_id, ok: true, text: `${next === 'PAUSED' ? 'Paused' : 'Resumed'} on ${c.label}.` });
      loadLive(c);
    } catch {
      setMessage({ key: c.action_campaign_id, ok: false, text: `Couldn't change the status on ${c.label}.` });
    } finally {
      setBusy(null);
    }
  };

  const setBudget = async (c: LiveCampaign, daily: number) => {
    if (!daily || daily <= 0) return;
    setBusy(c.action_campaign_id); setMessage(null);
    try {
      const { data: res } = await apiClient.patch(liveUrl(c, `budget?daily_budget=${daily}`));
      if (res?.updated === false) {
        setMessage({ key: c.action_campaign_id, ok: false, text: res.note ?? 'The platform did not accept the change.' });
      } else {
        setMessage({ key: c.action_campaign_id, ok: true, text: `Daily budget on ${c.label} set to ${formatMoney(daily, live[c.action_campaign_id]?.currency ?? c.currency)}.` });
        setBudgetDraft((b) => ({ ...b, [c.action_campaign_id]: '' }));
      }
      loadLive(c);
    } catch {
      setMessage({ key: c.action_campaign_id, ok: false, text: `Couldn't change the budget on ${c.label}.` });
    } finally {
      setBusy(null);
    }
  };

  const campaignFor = (rec: Recommendation) =>
    data?.campaigns.find((c) => c.action_campaign_id === rec.platform_campaign_id && c.platform === rec.platform);

  const applyRec = async (rec: Recommendation) => {
    const c = campaignFor(rec);
    if (!c || !rec.apply) return;
    if (rec.apply.type === 'pause') return setStatus(c, 'PAUSED');
    const current = live[c.action_campaign_id]?.daily_budget;
    if (current) await setBudget(c, Math.round(current * (1 + rec.apply.increase_pct / 100)));
  };

  // ── Rows ───────────────────────────────────────────────────────────────────
  const staticRows: AssignmentRow[] = (() => {
    const knownPlatforms =
      Object.keys(split).length > 0
        ? Object.keys(split)
        : (launch.accounts as any[])?.map((a: any) => a.platform).filter(Boolean) ?? ['meta'];
    const defaultStatus: AssignmentStatus =
      launch.timing?.type === 'scheduled' ? 'scheduled' : isOrganic ? 'published' : 'publishing';
    return knownPlatforms.map((p) => ({ key: p, platform: p, status: defaultStatus }));
  })();
  const displayRows = rows ?? staticRows;
  const liveByCampaign = new Map((data?.campaigns ?? []).map((c) => [c.platform_campaign_id, c]));
  const multi = (data?.campaigns.length ?? 0) > 1;

  // Headline numbers (C3 tiles): the summary across platforms, or the only campaign.
  const only = data?.campaigns.length === 1 ? data.campaigns[0] : null;
  const tiles = !isOrganic && data && (data.summary || only) ? {
    spend: data.summary?.spend ?? only?.spend ?? null,
    currency: data.summary?.currency ?? only?.currency ?? null,
    results: data.summary?.conversions ?? only?.result_count ?? 0,
    each: data.summary?.cost_per_result ?? only?.cost_per_result ?? null,
    ctr: data.summary?.ctr ?? only?.ctr ?? null,
    type: only?.result_type ?? null,
  } : null;
  const th = 'pb-3 pr-3 text-left text-label uppercase text-ink-3';
  const tile = 'flex flex-col gap-1.5 rounded-card bg-card p-[18px]';

  return (
    <div className="flex flex-col gap-6">
      {tiles && (
        <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(min(190px,100%),1fr))]">
          <div className={tile}><span className="text-label uppercase text-ink-3">Spent</span><span className="font-money text-[28px] font-semibold tabular">{tiles.spend !== null ? formatMoney(tiles.spend, tiles.currency) : 'Mixed'}</span><span className="text-xs text-ink-2">last 30 days · updates about hourly</span></div>
          <div className={tile}><span className="text-label uppercase text-ink-3">{resultNoun(tiles.type)}</span><span className="font-money text-[28px] font-semibold tabular">{formatCount(tiles.results)}</span><span className="text-xs text-ink-2">counted by the campaign’s goal</span></div>
          <div className={tile}><span className="text-label uppercase text-ink-3">{costPerLabel(tiles.type)}</span><span className="font-money text-[28px] font-semibold tabular">{formatMoney(tiles.each, tiles.currency)}</span><span className="text-xs text-ink-2">across platforms</span></div>
          <div className={tile}><span className="text-label uppercase text-ink-3">Click rate</span><span className="font-money text-[28px] font-semibold tabular">{formatPct(tiles.ctr)}</span><span className="text-xs text-ink-2">of people who saw it</span></div>
        </div>
      )}

      {/* Platforms — status, numbers and controls per platform (C3 table) */}
      <section aria-label="Platforms" className="overflow-x-auto rounded-panel bg-card px-6 pb-3 pt-5">
        <table className="w-full min-w-[760px] border-collapse">
          <thead>
            <tr className="border-b-[1.5px] border-b-ink">
              <th className={th}>Platform</th><th className={th}>Status</th><th className={th}>Daily budget</th>
              <th className={`${th} text-right`}>Spent</th><th className={`${th} text-right`}>Results</th><th className={`${th} text-right`}>Each</th><th className={th} />
            </tr>
          </thead>
          <tbody>
            {displayRows.map((row) => {
              const c = row.publishType !== 'organic_post' && row.platformCampaignId ? liveByCampaign.get(row.platformCampaignId) : undefined;
              if (!c) {
                return (
                  <tr key={row.key} className="border-b border-line last:border-b-0">
                    <td className="py-3 pr-3"><span className="flex items-center gap-2.5 font-bold text-ink"><PlatformIcon platform={row.platform} />{platformLabel(row.platform)}</span>
                      {row.accountName && <span className="block pl-9 text-xs text-ink-3">{row.accountName}</span>}</td>
                    <td className="py-3 pr-3"><StatusPill status={row.status} /></td>
                    <td className="py-3 pr-3 text-small text-ink-3">{row.publishType === 'organic_post' ? 'Post' : '—'}</td>
                    <td className="py-3 text-right text-ink-3">—</td><td className="py-3 text-right text-ink-3">—</td><td className="py-3 text-right text-ink-3">—</td>
                    <td className="py-3 pl-3 text-right">{row.postUrl && <a href={row.postUrl} target="_blank" rel="noopener noreferrer" className="text-small font-semibold text-brand-ink">View ↗</a>}</td>
                  </tr>
                );
              }
              const l = live[c.action_campaign_id];
              const loadingLive = liveLoading[c.action_campaign_id] ?? !(c.action_campaign_id in live);
              const cur = l?.currency ?? c.currency;
              const isPaused = (l?.status ?? '').toUpperCase() === 'PAUSED';
              const isBusy = busy === c.action_campaign_id;
              const draft = budgetDraft[c.action_campaign_id] ?? '';
              return (
                <React.Fragment key={row.key}>
                  <tr className="border-b border-line align-middle">
                    <td className="py-3 pr-3">
                      <span className="flex items-center gap-2.5 font-bold text-ink"><PlatformIcon platform={c.analytics_platform} />{c.label}</span>
                      <span className="block pl-9 text-xs text-ink-3">{[c.account_name, `${c.days_running} days running`].filter(Boolean).join(' · ')}</span>
                    </td>
                    <td className="py-3 pr-3"><LiveStatus live={l} loading={loadingLive} /></td>
                    <td className="py-3 pr-3">
                      <span className="flex flex-wrap items-center gap-2 text-sm">
                        {l?.daily_budget != null ? <span className="font-money font-semibold">{formatMoney(l.daily_budget, cur)}</span>
                          : l?.lifetime_budget != null ? <span className="text-xs text-ink-2">{formatMoney(l.lifetime_budget, cur)} lifetime · change it on the platform</span> : <span className="text-ink-3">—</span>}
                        {l?.budget_editable && (
                          <span className="flex items-center gap-1.5">
                            <input type="number" min={1} placeholder="New daily" aria-label={`New daily budget for ${c.label}`} value={draft}
                              onChange={(e) => setBudgetDraft((bd) => ({ ...bd, [c.action_campaign_id]: e.target.value }))}
                              className="h-8 w-24 rounded-lg border-0 bg-ground px-2 text-xs shadow-ring-line focus:outline-none focus:shadow-ring-ink" />
                            <button type="button" disabled={isBusy || !Number(draft)} onClick={() => setBudget(c, Number(draft))}
                              className="h-8 rounded-lg px-2.5 font-display text-xs font-bold text-ink shadow-ring-line hover:shadow-ring-ink disabled:opacity-50">Change</button>
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="py-3 text-right font-money font-semibold">{c.has_data ? formatMoney(c.spend, cur) : '—'}</td>
                    <td className="py-3 text-right font-money font-semibold">{c.has_data ? formatCount(c.result_count) : '—'}</td>
                    <td className="py-3 text-right font-money font-semibold">{c.has_data ? formatMoney(c.cost_per_result, cur) : '—'}</td>
                    <td className="py-3 pl-3 text-right">
                      <button type="button" disabled={isBusy || loadingLive || !l} onClick={() => setStatus(c, isPaused ? 'ACTIVE' : 'PAUSED')}
                        title={!l && !loadingLive ? "Couldn't read the campaign from the platform" : undefined}
                        className="inline-flex h-9 items-center rounded-btn px-3 font-display text-xs font-bold text-ink shadow-ring-line hover:shadow-ring-ink disabled:opacity-50">
                        {isBusy ? '…' : isPaused ? 'Resume' : 'Pause'}
                      </button>
                    </td>
                  </tr>
                  {(message?.key === c.action_campaign_id || !c.has_data) && (
                    <tr><td colSpan={7} className={`pb-3 text-xs ${message?.key === c.action_campaign_id ? (message.ok ? 'text-ok' : 'text-warn') : 'text-ink-3'}`}>
                      {message?.key === c.action_campaign_id ? message.text : 'No delivery reported yet. Numbers appear within a few hours of the ads serving.'}
                    </td></tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        {multi && data?.summary && (
          <div className="flex flex-wrap gap-x-4 gap-y-1 pt-3 text-xs text-ink-2">
            <span className="font-semibold text-ink">All platforms</span>
            {data.summary.spend !== null ? <span>{formatMoney(data.summary.spend, data.summary.currency)} spent</span> : <span>Spend in several currencies</span>}
            <span>{formatCount(data.summary.conversions)} results</span>
            {data.summary.cost_per_result !== null && <span>{formatMoney(data.summary.cost_per_result, data.summary.currency)} per result</span>}
          </div>
        )}
        <div className="pt-2 text-xs text-ink-3">Numbers are the last 30 days and update about hourly.</div>
      </section>

      <div className="grid items-start gap-4 [grid-template-columns:repeat(auto-fit,minmax(min(420px,100%),1fr))]">
        {/* Which ad is winning (creative test) */}
        {concepts.length > 0 && !isOrganic && (
          <section className="flex flex-col gap-1 rounded-panel bg-card p-[22px]">
            <h2 className="m-0 mb-2 font-display text-card-title font-extrabold">Which ad is winning</h2>
            {(data?.creative_tests?.length ?? 0) === 0 ? (
              <>
                {concepts.map((c, i) => (
                  <div key={c.id} className={`flex items-center justify-between gap-3 py-3 ${i === 0 ? 'border-t-[1.5px] border-t-ink' : 'border-t border-line'}`}>
                    <span className="font-semibold">{c.concept ?? c.id}</span>
                    <span className="rounded-full bg-warn-soft px-2.5 py-0.5 text-xs font-semibold text-warn">No results yet</span>
                  </div>
                ))}
                <p className="m-0 pt-2 text-xs text-ink-3">Results per ad appear once the ads have served (collected for Meta).</p>
              </>
            ) : data!.creative_tests.map((t) => {
              const winner = t.creatives.find((c) => c.verdict === 'winner' || c.verdict === 'leading');
              const headline = t.status === 'winner' && winner ? `${conceptName[winner.concept_id] ?? winner.concept_id} gets ${t.result_type} cheapest.`
                : t.status === 'leading' ? 'The leaders are close; keep running before moving budget.'
                : t.status === 'collecting' ? `Too early to call: compare once each ad has about 10 ${t.result_type}.` : 'No ad results yet.';
              return (
                <div key={t.platform} className="flex flex-col">
                  <p className="m-0 mb-2 text-small text-ink-2">{data!.creative_tests.length > 1 && <b className="text-ink">{t.label}: </b>}{headline}</p>
                  {t.creatives.map((c, i) => (
                    <div key={c.concept_id} className={`grid grid-cols-[minmax(0,1fr)_96px_64px] items-center gap-3 py-3 ${i === 0 ? 'border-t-[1.5px] border-t-ink' : 'border-t border-line'}`}>
                      <div className="flex min-w-0 flex-col gap-1">
                        <span className="truncate font-semibold">{conceptName[c.concept_id] ?? c.concept_id}</span>
                        <span className={`self-start rounded-full px-2.5 py-0.5 text-xs font-semibold ${VERDICT[c.verdict].cls}`}>{VERDICT[c.verdict].label}</span>
                      </div>
                      <div className="flex flex-col gap-1"><span className="font-money text-small font-semibold">{c.spend_share}%</span>
                        <div className="h-1.5 rounded-full bg-inset"><div className="h-full rounded-full bg-ink" style={{ width: `${c.spend_share}%` }} /></div></div>
                      <span className="text-right font-money text-small">{formatPct(c.ctr)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between border-t border-line pt-1.5 text-[11px] text-ink-3"><span>Ad</span><span>Share of spend · click rate</span></div>
                </div>
              );
            })}
          </section>
        )}

        <div className="flex flex-col gap-4">
          {/* Leads captured from these ads */}
          {!isOrganic && data && (data.leads.total > 0 || data.leads.calls > 0) && (
            <section className="flex flex-wrap items-center gap-3 rounded-panel bg-card p-[22px]">
              <div className="flex-1">
                <h2 className="m-0 font-display text-card-title font-extrabold">Leads</h2>
                <p className="m-0 text-small text-ink-2">
                  {formatCount(data.leads.total)} {data.leads.total === 1 ? 'lead' : 'leads'}
                  {data.leads.new > 0 && <> · <b className="text-brand-ink">{formatCount(data.leads.new)} new</b></>}
                  {data.leads.calls > 0 && <> · {formatCount(data.leads.calls)} {data.leads.calls === 1 ? 'call' : 'calls'}</>}
                </p>
              </div>
              <button type="button" onClick={() => navigate(`/leads?conversation=${conversationId}`)}
                className="inline-flex min-h-[44px] items-center rounded-btn bg-navy px-4 font-display text-sm font-bold text-on-navy hover:bg-navy-3">Leads inbox</button>
            </section>
          )}

          {/* Suggestions — only actions Admeru can carry out get a button */}
          {!isOrganic && (data?.recommendations ?? []).map((r) => {
            const c = campaignFor(r);
            const current = c ? live[c.action_campaign_id]?.daily_budget : null;
            const target = r.apply?.type === 'budget' && current ? Math.round(current * (1 + r.apply.increase_pct / 100)) : null;
            const cur = c ? live[c.action_campaign_id]?.currency ?? c.currency : null;
            return (
              <section key={r.id} className="flex flex-col gap-3 rounded-panel bg-navy p-[22px] text-on-navy">
                <div className="text-label uppercase text-on-navy-brand">Suggestion · {platformLabel(r.platform)}</div>
                <div className="font-display text-lg font-bold leading-snug">{r.title}</div>
                <div className="text-small text-on-navy-2">{r.reason}</div>
                {r.apply?.type === 'pause' && (
                  <button type="button" disabled={!c || busy === c.action_campaign_id} onClick={() => applyRec(r)}
                    className="inline-flex min-h-[44px] w-max items-center rounded-btn bg-brand px-4 font-display text-sm font-bold text-brand-on hover:bg-brand-hover disabled:opacity-50">
                    Pause the {platformLabel(r.platform)} campaign
                  </button>
                )}
                {r.apply?.type === 'budget' && (target ? (
                  <button type="button" disabled={!c || busy === c.action_campaign_id} onClick={() => applyRec(r)}
                    className="inline-flex min-h-[44px] w-max items-center rounded-btn bg-brand px-4 font-display text-sm font-bold text-brand-on hover:bg-brand-hover disabled:opacity-50">
                    Raise to {formatMoney(target, cur)}/day (+{r.apply.increase_pct}%)
                  </button>
                ) : <p className="m-0 text-xs text-on-navy-3">Change the budget in the platform row once its current budget loads.</p>)}
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
};
